import React, { useState } from 'react';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  CleaningServices as CleanupIcon,
} from '@mui/icons-material';
import { Box, Collapse, Typography } from '@mui/material';

import { useCardExpansion } from '../../store/hooks';
import {
  type ActionCommand,
  ActionCommandType,
  type BaseAction,
  type CardContentProps,
  type TemplateCleanupPayload,
} from '../../types';
import { TemplateCleanupForm } from '../edit/TemplateCleanupForm';

import CardContent from './base/CardContent';

const CONTAINER_NAME = 'template-cleanup-card';

interface TemplateCleanupCardContentProps extends CardContentProps {
  action: BaseAction<'template_cleanup'>;
}

const TemplateCleanupCardContent: React.FC<TemplateCleanupCardContentProps> = ({
  action,
  onCommand,
}) => {
  const [editedPayload, setEditedPayload] = useState<TemplateCleanupPayload>(action.action_payload);
  const { isExpanded } = useCardExpansion();
  const expanded = isExpanded(action.action_id);

  const content = (
    <>
      <Typography variant="body2" color="text.secondary">
        <strong>{action.action_payload.template_name}</strong>
      </Typography>
      <Collapse in={!expanded} timeout="auto" unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          You rarely use these sections—pick any to remove.
        </Typography>
        <Box sx={{ mt: 1 }}>
          {action.action_payload.sections.map(section => (
            <Box
              key={section.module_id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.25,
              }}
            >
              {section.should_remove ? (
                <CheckBox sx={{ fontSize: 16, color: 'primary.main' }} />
              ) : (
                <CheckBoxOutlineBlank sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.8125rem',
                  lineHeight: 1.2,
                }}
              >
                {section.display_name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </>
  );

  const expandableContent = (
    <TemplateCleanupForm payload={editedPayload} onChange={setEditedPayload} readOnly={false} />
  );

  return (
    <CardContent
      containerName={CONTAINER_NAME}
      id={action.action_id}
      title={'Clean up template?'}
      content={content}
      avatarIcon={<CleanupIcon />}
      onCommand={(command: ActionCommand) => {
        if (command.type === ActionCommandType.ACCEPT) {
          if (expanded) {
            // Use ACCEPT_WITH_EDITS for edited payload
            onCommand({
              type: ActionCommandType.ACCEPT_WITH_EDITS,
              payload: editedPayload,
            });
          } else {
            onCommand({ type: ActionCommandType.ACCEPT });
          }
        } else {
          onCommand(command);
        }
      }}
      expandable={true}
      expandableContent={expandableContent}
      isExpandableContentEditable={true}
    />
  );
};

export default TemplateCleanupCardContent;
